/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\emailTemplatesApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the email-templates module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';

export interface EmailTemplate {
    id: number;
    uuid: string;
    name: string;
    slug: string;
    subject: string;
    fromName?: string;
    fromEmail?: string;
    htmlContent: string;
    textContent: string | null;
    variables: { name: string; description: string }[];
    isActive: boolean;
    tenantId: number;
    createdAt: string;
    updatedAt: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getEmailTemplates = async (filters: any = {}): Promise<EmailTemplate[]> => {
    const query = new URLSearchParams(filters).toString();
    const response = await fetch(buildApiUrl(`/api/email-templates?${query}`), {
        headers: getApiHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch email templates');
    return response.json();
};

export const getEmailTemplateById = async (id: number | string): Promise<EmailTemplate> => {
    const response = await fetch(buildApiUrl(`/api/email-templates/${id}`), {
        headers: getApiHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch email template');
    return response.json();
};

export const createEmailTemplate = async (data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await fetch(buildApiUrl('/api/email-templates'), {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create email template');
    return response.json();
};

export const updateEmailTemplate = async (id: number | string, data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await fetch(buildApiUrl(`/api/email-templates/${id}`), {
        method: 'PUT',
        headers: getApiHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update email template');
    return response.json();
};

export const deleteEmailTemplate = async (id: number | string): Promise<void> => {
    const response = await fetch(buildApiUrl(`/api/email-templates/${id}`), {
        method: 'DELETE',
        headers: getApiHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete email template');
};
